
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Project, Subsidiary, Department, User } from "@/entities/all";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, Search, Grid, List, MoreHorizontal, TrendingUp, TrendingDown, Check, X, Edit, Eye, Archive, Copy, Share2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import ProjectForm from "../components/project/ProjectForm";
import ProjectGridView from "../components/project/ProjectGridView";
import { Label } from "@/components/ui/label";

const statusConfig = {
    draft: { label: "Brouillon", color: "bg-gray-100 text-gray-800", icon: <div className="h-2 w-2 rounded-full bg-gray-400" /> },
    planning: { label: "Planification", color: "bg-blue-100 text-blue-800", icon: <div className="h-2 w-2 rounded-full bg-blue-400" /> },
    validation: { label: "En attente", color: "bg-yellow-100 text-yellow-800", icon: <div className="h-2 w-2 rounded-full bg-yellow-400" /> },
    approved: { label: "Approuvé", color: "bg-green-100 text-green-800", icon: <Check className="w-3 h-3 text-green-500" /> },
    in_progress: { label: "En cours", color: "bg-teal-100 text-teal-800", icon: <div className="h-2 w-2 rounded-full bg-teal-400" /> },
    completed: { label: "Terminé", color: "bg-purple-100 text-purple-800", icon: <Check className="w-3 h-3 text-purple-500" /> },
    suspended: { label: "Retardé", color: "bg-red-100 text-red-800", icon: <X className="w-3 h-3 text-red-500" /> }
};

const priorityConfig = {
    low: { label: "Faible", color: "bg-gray-200 text-gray-700", indicator: "bg-gray-400" },
    medium: { label: "Normale", color: "bg-blue-200 text-blue-700", indicator: "bg-blue-500" },
    high: { label: "Haute", color: "bg-orange-200 text-orange-700", indicator: "bg-orange-500" },
    urgent: { label: "Critique", color: "bg-red-200 text-red-700", indicator: "bg-red-500" }
};

// @ts-nocheck
export default function ProjectListing() {
    const [projects, setProjects] = useState([]);
    const [filteredProjects, setFilteredProjects] = useState([]);
    const [subsidiaries, setSubsidiaries] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [users, setUsers] = useState([]);
    const [viewMode, setViewMode] = useState("list");
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [subsidiaryFilter, setSubsidiaryFilter] = useState("all");
    const [departmentFilter, setDepartmentFilter] = useState("all");
    const [isLoading, setIsLoading] = useState(true);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingProject, setEditingProject] = useState(null);
    const navigate = useNavigate();

    const filterProjects = useCallback(() => {
        let currentFilteredProjects = projects;

        if (searchTerm) {
            currentFilteredProjects = currentFilteredProjects.filter(project =>
                project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                project.code?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (statusFilter !== "all") {
            currentFilteredProjects = currentFilteredProjects.filter(project => project.status === statusFilter);
        }

        if (subsidiaryFilter !== "all") {
            currentFilteredProjects = currentFilteredProjects.filter(project => {
                const sub = subsidiaries.find(s => s.id === project.subsidiary_id);
                return sub?.name === subsidiaryFilter;
            });
        }

        if (departmentFilter !== "all") {
            currentFilteredProjects = currentFilteredProjects.filter(project => {
                const dept = departments.find(d => d.id === project.department_id);
                return dept?.name === departmentFilter;
            });
        }

        setFilteredProjects(currentFilteredProjects);
    }, [projects, searchTerm, statusFilter, subsidiaryFilter, departmentFilter, subsidiaries, departments]);

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        filterProjects();
    }, [filterProjects]); // Dependency array now includes the memoized filterProjects

    const loadData = async () => {
        setIsLoading(true);
        try {
            const [projectsData, subsidiariesData, departmentsData, usersData] = await Promise.all([
                Project.list("-created_date"),
                Subsidiary.list(),
                Department.list(),
                User.list().catch(() => [])
            ]);
            setProjects(projectsData);
            setSubsidiaries(subsidiariesData);
            setDepartments(departmentsData);
            setUsers(usersData);
        } catch (error) {
            console.error("Failed to load data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFormSubmit = async (data) => {
        try {
            if (editingProject) {
                // Update existing project
                await Project.update(editingProject.id, data);
            } else {
                // Create new project
                const projectCode = `CAPEX-${new Date().getFullYear()}-${String(projects.length + 1).padStart(3, '0')}`;
                const projectData = {
                    ...data,
                    code: projectCode,
                    status: 'draft',
                    progress: 0
                };
                await Project.create(projectData);
            }
            setIsFormOpen(false);
            setEditingProject(null);
            loadData();
        } catch (error) {
            console.error("Failed to save project:", error);
        }
    };

    const handleCreateProject = () => {
        setEditingProject(null);
        setIsFormOpen(true);
    };

    const handleEditProject = (project) => {
        setEditingProject(project);
        setIsFormOpen(true);
    };

    const handleViewProject = (project) => {
        navigate(createPageUrl('ProjectDetail', { params: { id: project.id } }));
    };

    const handleArchiveProject = async (project) => {
        if (window.confirm(`Êtes-vous sûr de vouloir archiver le projet "${project.name}" ?`)) {
            try {
                await Project.update(project.id, { status: 'cancelled' });
                loadData();
            } catch (error) {
                console.error("Failed to archive project:", error);
            }
        }
    };

    const getSubsidiary = (id) => subsidiaries.find(s => s.id === id);
    const getDepartment = (id) => departments.find(d => d.id === id);

    const formatBudget = (amount) => {
        if (!amount && amount !== 0) return "0€";
        if (amount >= 1000000) return `${(amount / 1000000).toFixed(1).replace('.', ',')} M€`;
        if (amount >= 1000) return `${(amount / 1000).toFixed(0)} K€`;
        return `${amount.toLocaleString('fr-FR')} €`;
    };

    const budgetVariance = (budget, consumed) => {
        if (budget === null || budget === undefined || budget === 0) return { value: 0, color: 'text-gray-500', Icon: null };
        if (consumed === null || consumed === undefined) return { value: 0, color: 'text-gray-500', Icon: null };

        const variance = consumed - budget;
        const percentage = (variance / budget * 100);
        const isOver = variance > 0;
        return {
            value: percentage,
            color: isOver ? 'text-red-500' : 'text-green-500',
            Icon: isOver ? TrendingUp : TrendingDown,
        };
    };

    const selectedSubsidiaryObj = subsidiaries.find(s => s.name === subsidiaryFilter);

    const availableDepartments = departments.filter(d => {
        if (!selectedSubsidiaryObj) return true;
        return d.subsidiary_number === selectedSubsidiaryObj.subsidiary_number;
    });

    if (isLoading) {
        return (
            <div className="p-8">
                <div className="animate-pulse space-y-6">
                    <div className="h-8 bg-gray-200 rounded w-64"></div>
                    <div className="grid grid-cols-4 gap-4">
                        {[...Array(4)].map((_, i) => (
                            <div key={i} className="h-12 bg-gray-200 rounded"></div>
                        ))}
                    </div>
                    <div className="h-96 bg-gray-200 rounded"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-8 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
            {isFormOpen && (
                <ProjectForm
                    isOpen={isFormOpen}
                    onClose={() => {
                        setIsFormOpen(false);
                        setEditingProject(null);
                    }}
                    onSubmit={handleFormSubmit}
                    subsidiaries={subsidiaries}
                    departments={departments}
                    project={editingProject}
                    users={users}
                />
            )}

            <div className="max-w-screen-2xl mx-auto">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Projets CAPEX</h1>
                        <p className="text-gray-600 mt-1">Gestion et suivi des investissements industriels</p>
                    </div>
                    <Button className="bg-teal-600 hover:bg-teal-700 gap-2" onClick={handleCreateProject}>
                        <Plus className="w-4 h-4" />
                        Nouveau Projet
                    </Button>
                </div>

                <Card className="mb-6 border-0 shadow-lg">
                    <CardContent className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 items-end">
                            <div className="relative lg:col-span-2">
                                <Label className="text-sm font-medium text-gray-700">Recherche</Label>
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 mt-3" />
                                <Input
                                    placeholder="Rechercher par nom, code..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 mt-1"
                                />
                            </div>

                            <div className="space-y-1">
                                <Label className="text-sm font-medium text-gray-700">Filiale</Label>
                                <Select value={subsidiaryFilter} onValueChange={(value) => {
                                    setSubsidiaryFilter(value);
                                    setDepartmentFilter("all");
                                }}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Toutes" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Toutes les filiales</SelectItem>
                                        {subsidiaries.map(subsidiary => (
                                            <SelectItem key={subsidiary.id} value={subsidiary.name}>
                                                {subsidiary.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-1">
                                <Label className="text-sm font-medium text-gray-700">Département</Label>
                                <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Tous" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tous les départements</SelectItem>
                                        {availableDepartments.map(department => (
                                            <SelectItem key={department.id} value={department.name}>
                                                {department.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-1">
                                <Label className="text-sm font-medium text-gray-700">Statut</Label>
                                <Select value={statusFilter} onValueChange={setStatusFilter}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Tous" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tous les statuts</SelectItem>
                                        {Object.entries(statusConfig).map(([key, config]) => (
                                            <SelectItem key={key} value={key}>{config.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="flex gap-2 justify-end">
                                <Button
                                    variant={viewMode === "list" ? "default" : "outline"}
                                    size="icon"
                                    onClick={() => setViewMode("list")}
                                    className={viewMode === "list" ? "bg-teal-600 hover:bg-teal-700" : ""}
                                >
                                    <List className="w-4 h-4" />
                                </Button>
                                <Button
                                    variant={viewMode === "grid" ? "default" : "outline"}
                                    size="icon"
                                    onClick={() => setViewMode("grid")}
                                    className={viewMode === "grid" ? "bg-teal-600 hover:bg-teal-700" : ""}
                                >
                                    <Grid className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {viewMode === "list" && (
                    <Card className="border-0 shadow-lg">
                        <CardContent className="p-0">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-gray-50 hover:bg-gray-50">
                                        <TableHead className="w-[350px]">Projet</TableHead>
                                        <TableHead>Filiale / Département</TableHead>
                                        <TableHead>Type</TableHead>
                                        <TableHead>Budget</TableHead>
                                        <TableHead>Progression</TableHead>
                                        <TableHead>Statut</TableHead>
                                        <TableHead className="w-[100px]">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredProjects.map((project) => {
                                        const status = statusConfig[project.status] || statusConfig.draft;
                                        const priority = priorityConfig[project.priority] || priorityConfig.medium;
                                        const sub = getSubsidiary(project.subsidiary_id);
                                        const dept = getDepartment(project.department_id);
                                        const bv = budgetVariance(project.total_budget, project.budget_consumed);

                                        return (
                                            <TableRow
                                                key={project.id}
                                                className="hover:bg-gray-50 cursor-pointer transition-colors"
                                                onClick={() => handleViewProject(project)}
                                            >
                                                <TableCell>
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-1 h-12 rounded ${priority.indicator}`} />
                                                        <div>
                                                            <p className="font-semibold text-gray-900 hover:text-teal-600 transition-colors">
                                                                {project.name}
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                {project.code} • {new Date(project.created_date).toLocaleDateString('fr-FR')}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        {sub && (
                                                            <div
                                                                className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                                                                style={{backgroundColor: sub.color}}
                                                            >
                                                                {sub.code}
                                                            </div>
                                                        )}
                                                        <div>
                                                            <p className="font-medium text-gray-800">{sub?.name || "Non défini"}</p>
                                                            <p className="text-sm text-gray-600">{dept?.name || "Non défini"}</p>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                          <span className="text-sm text-gray-700 capitalize">
                            {project.type?.replace(/_/g, ' ') || "Non défini"}
                          </span>
                                                </TableCell>
                                                <TableCell>
                                                    <div>
                                                        <p className="font-semibold">{formatBudget(project.total_budget)}</p>
                                                        {bv.Icon && bv.value !== 0 && (
                                                            <div className={`flex items-center gap-1 text-xs ${bv.color}`}>
                                                                <bv.Icon className="w-3 h-3"/>
                                                                {bv.value > 0 ? '+' : ''}{bv.value.toFixed(1)}%
                                                            </div>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <Progress value={project.progress || 0} className="w-20 h-2" />
                                                        <span className="text-sm font-medium w-10">{project.progress || 0}%</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge className={`${status.color} gap-1`}>
                                                        {status.icon}
                                                        {status.label}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="h-8 w-8"
                                                            >
                                                                <MoreHorizontal className="w-4 h-4" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            <DropdownMenuItem
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleViewProject(project);
                                                                }}
                                                                className="flex items-center gap-2"
                                                            >
                                                                <Eye className="w-4 h-4" />
                                                                Voir le détail
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleEditProject(project);
                                                                }}
                                                                className="flex items-center gap-2"
                                                            >
                                                                <Edit className="w-4 h-4" />
                                                                Modifier
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem className="flex items-center gap-2">
                                                                <Copy className="w-4 h-4" />
                                                                Dupliquer
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem className="flex items-center gap-2">
                                                                <Share2 className="w-4 h-4" />
                                                                Partager
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleArchiveProject(project);
                                                                }}
                                                                className="flex items-center gap-2 text-red-600"
                                                            >
                                                                <Archive className="w-4 h-4" />
                                                                Archiver
                                                            </DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>

                            {filteredProjects.length === 0 && (
                                <div className="text-center py-12">
                                    <p className="text-gray-500">Aucun projet trouvé pour les filtres actuels</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {viewMode === 'grid' && (
                    <ProjectGridView
                        projects={filteredProjects}
                        subsidiaries={subsidiaries}
                        departments={departments}
                        onView={handleViewProject}
                        onEdit={handleEditProject}
                        onArchive={handleArchiveProject}
                        formatBudget={formatBudget}
                        statusConfig={statusConfig}
                        priorityConfig={priorityConfig}
                    />
                )}
            </div>
        </div>
    );
}
